import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, MapPin, AudioLines } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AudioSettingsFields } from "../../components/AudioSettingsFields";

const EMPTY = { name: "", address: "", notes: "" };

export function SitesTab() {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [audioTarget, setAudioTarget] = useState(null);
  const [audioForm, setAudioForm] = useState(null);
  const [audioSaving, setAudioSaving] = useState(false);

  const load = () => api.get("/admin/sites").then((res) => setSites(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, address: s.address || "", notes: s.notes || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/sites/${editing.id}`, form);
      else await api.post("/admin/sites", form);
      toast.success(editing ? "Site updated" : "Site created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openAudio = async (s) => {
    try {
      const res = await api.get("/admin/settings");
      setAudioForm(s.audio || res.data.audio);
      setAudioTarget(s);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveAudio = async (clear = false) => {
    setAudioSaving(true);
    try {
      await api.put(`/admin/sites/${audioTarget.id}/audio`, { audio: clear ? null : audioForm });
      toast.success(clear ? `Site audio reset to global defaults` : `Audio settings saved for ${audioTarget.name}`);
      setAudioTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAudioSaving(false);
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/admin/sites/${s.id}`);
      toast.success("Site deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="sites-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-100">Sites</h3>
        <Button data-testid="create-site-btn" onClick={openCreate} size="sm"
          className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New Site
        </Button>
      </div>

      {sites.length === 0 ? (
        <div className="ops-card p-8 text-center text-slate-500 text-sm" data-testid="sites-empty">
          No sites yet. Create your first building or property.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sites.map((s) => (
            <div key={s.id} className="ops-card p-5 hover:border-[#2A364F] transition-colors" data-testid={`site-card-${s.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                    <Building2 className="w-4.5 h-4.5 text-cyan-400" />
                  </div>
                  <div className="font-semibold text-slate-100">{s.name}</div>
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`audio-site-${s.id}`} variant="ghost" size="icon" title="Site audio settings"
                    onClick={() => openAudio(s)}
                    className={`h-8 w-8 ${s.audio ? "text-emerald-400 hover:text-emerald-300" : "text-slate-400 hover:text-slate-100"}`}>
                    <AudioLines className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`edit-site-${s.id}`} variant="ghost" size="icon" onClick={() => openEdit(s)}
                    className="h-8 w-8 text-slate-400 hover:text-slate-100"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`delete-site-${s.id}`} variant="ghost" size="icon" onClick={() => remove(s)}
                    className="h-8 w-8 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              {s.address && <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-1"><MapPin className="w-3 h-3" />{s.address}</div>}
              {s.notes && <div className="text-xs text-slate-500 mt-2">{s.notes}</div>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!audioTarget} onOpenChange={() => setAudioTarget(null)}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="site-audio-dialog">
          <DialogHeader><DialogTitle>Audio Settings — {audioTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">
            Overrides the global Audio Quality settings for this site only. Applied to Asterisk on save.
            {audioTarget?.audio
              ? <span className="text-emerald-400"> This site has a custom override active.</span>
              : <span> Currently using the global defaults.</span>}
          </p>
          <AudioSettingsFields audio={audioForm} setAudio={setAudioForm} idPrefix="site-audio" />
          <div className="flex gap-2 pt-2">
            <Button data-testid="site-audio-save-btn" onClick={() => saveAudio(false)} disabled={audioSaving || !audioForm?.codecs?.length}
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              {audioSaving ? "Saving…" : "Save Site Audio"}
            </Button>
            {audioTarget?.audio && (
              <Button data-testid="site-audio-clear-btn" variant="outline" onClick={() => saveAudio(true)} disabled={audioSaving}
                className="border-[#1E2638] bg-transparent hover:bg-[#1E2638] text-slate-300">
                Use Global Defaults
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="site-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Site" : "New Site"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input data-testid="site-name-input" value={form.name} required
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0A0D14] border-[#1E2638]" placeholder="Grand Crest Towers" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Address</Label>
              <Input data-testid="site-address-input" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="bg-[#0A0D14] border-[#1E2638]" placeholder="12 Meridian Avenue, London" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Notes</Label>
              <Input data-testid="site-notes-input" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-[#0A0D14] border-[#1E2638]" placeholder="Optional" />
            </div>
            <Button type="submit" data-testid="site-submit-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              {editing ? "Save Changes" : "Create Site"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
